import type { FunctionComponent } from "react";
import {
  Icon,
  Price,
  SkeletonLoader,
  StatusTag,
  type SkeletonLoaderProps,
} from "@kanda-libs/ks-design-library";
import usePriceStatusProps from "components/job/JobTable/PriceStatus/usePriceStatusProps";
import type {
  FormattedFinanceStatus,
  FormattedStatus,
} from "components/job/JobTable/types";
import { STATUS_TAG_PROPS } from "components/job/JobTable/PriceStatus/constants";
import clsx from "clsx";

export interface PriceStatusProps {
  /**
   * Job total price
   */
  total: number;
  /**
   * Job status
   */
  status?: string;
  /**
   * Quoted to
   */
  quotedTo?: string;
  /**
   * Job finance status
   */
  financeStatus?: string;
  skeletonClassName?: string;
  skeletonProps?: SkeletonLoaderProps;
  isLoading?: boolean;
  /**
   * Displayed inline in a table
   */
  inline?: boolean;
}

const PriceStatus: FunctionComponent<PriceStatusProps> = ({
  total,
  status = "draft",
  quotedTo = "",
  financeStatus = "not_applied",
  skeletonClassName = "",
  skeletonProps = {},
  isLoading = false,
  inline = true,
}) => {
  const { color, classNames, financed, signed, pending, paid } =
    usePriceStatusProps(
      status as FormattedStatus,
      financeStatus as FormattedFinanceStatus
    );

  const wrapperClasses = clsx(
    "flex flex-row max-w-full",
    inline ? "" : "-mt-2"
  );

  return (
    <SkeletonLoader
      isLoading={isLoading}
      className={skeletonClassName}
      {...skeletonProps}
      afterLoading={
        <div className={wrapperClasses}>
          {status === "draft" && (
            <StatusTag {...STATUS_TAG_PROPS} size={21} variant="solid" />
          )}
          {quotedTo && (
            <StatusTag
              {...STATUS_TAG_PROPS}
              status="lead"
              size={21}
              variant="solid"
            />
          )}
          <Price amount={total} color={color} {...classNames} />
          {financed && (
            <Icon
              icon="financed"
              stroke={color}
              size={16}
              className="my-auto ml-2"
            />
          )}
          {signed && (
            <Icon
              icon="job"
              stroke="green-500"
              size={16}
              className="my-auto ml-2"
            />
          )}
          {pending && (
            <Icon
              icon="clock"
              stroke="green-500"
              size={16}
              className="my-auto ml-2"
            />
          )}
          {paid && (
            <Icon
              icon="check"
              stroke="green-500"
              size={16}
              className="my-auto ml-2"
            />
          )}
        </div>
      }
    />
  );
};

export default PriceStatus;
